// Copyright (c) Docugami, Inc. All rights reserved.

import { PrimaryButton } from "@fluentui/react";
import { useEffect, useState } from 'react';
import { Config } from '../../../config';
import styled from 'styled-components';
import { DgVerticalSpacer, DgNonSelectableLabelCss, DgFlexCenteredColumn } from '../../common/DgCommonStyledComponents';
import { ServerSideRedirect } from '../../assisted_authoring/AaTaskpaneMenu';
import { StatusBar } from '../../file_management/status-bar';
import { Logger, LoggerCategory } from 'utils/Logger';
import { dgFontFamily } from "components/common/Fonts";
import { useNavigationService } from "hooks/navigationService";
import { useDgAuth } from "hooks/DgAuthentication";

const LoginContainer = styled.div`
    flex: 1;
    height: 100%;
    width: 100%;
    overflow: auto;
    background: 'white';
`;

const TaglineText = styled.div`
    font-family: ${dgFontFamily};
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    ${DgNonSelectableLabelCss}
    color: #000000;
    width: 280px;
    padding-top: 25px;
`;

const LoginButtonText = styled.span`
    font-family: ${dgFontFamily};
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 22px;
    text-align: center;
    ${DgNonSelectableLabelCss}
    color: #FFFFFF;
`;

const SignupText = styled.div`
    font-family: ${dgFontFamily};
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 24px;
    text-align: center;
    ${DgNonSelectableLabelCss}
    color: #000000;
    cursor: pointer;
`;

const LogoPlaceholder = styled.div`
    height: 160px;
    width: 160px;
`;

export interface OfficeOnlineAuthTaskpaneProps {
    logout: boolean;
}

export const OfficeOnlineAuthTaskpane = (props: OfficeOnlineAuthTaskpaneProps): JSX.Element => {
    enum OfficeLoginStep {
        ShowLoginButton,
        LoginDialogShowing,
        Logout
    }

    const { isAuthenticated } = useDgAuth();

    const navService = useNavigationService()

    const [loginStep, setLoginStep] = useState<OfficeLoginStep>(props.logout ? OfficeLoginStep.Logout : OfficeLoginStep.ShowLoginButton);

    useEffect(() => {
        if (!!Office.context.ui.messageParent) {
            // oops... we shouldn't be here in the dialog
            // this is probably due to a logout.  let's get this closed.
            Office.context.ui.messageParent(String(false));
        }
    }, [])

    useEffect(() => {
        switch (loginStep) {
            case OfficeLoginStep.Logout:
                showOfficeDialog(`${Config.baseUrl}/word/dialog/auth/logout`);
                break;
        }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loginStep])

    useEffect(() => {
        if (isAuthenticated) {
            navService.navToWordTaskpane();
        }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated])

    async function onOfficeDialogComplete(message: any, dialog: Office.Dialog) {
        dialog.close();
        try {
            window.location.href = Config.baseUrl
        }
        catch(error) {
            Logger.LogError(LoggerCategory.Network, "Failed AA redirect callback", error);
            setLoginStep(OfficeLoginStep.ShowLoginButton);
        }
    }

    function showOfficeDialog(url: string): void {
        setLoginStep(OfficeLoginStep.LoginDialogShowing);
        const Office = (window as any).Office;
        Office.context.ui.displayDialogAsync(
            url,
            { height: 65, width: 25, displayInIframe: false },
            function (asyncResult: any) {
                if (asyncResult.status === Office.AsyncResultStatus.Failed) {
                    setLoginStep(OfficeLoginStep.ShowLoginButton);
                    console.error(`Office Dialog Failed: ${asyncResult.error.code}: + ${asyncResult.error.message}`);
                } else {
                    var dialog = asyncResult.value;
                    dialog.addEventHandler(Office.EventType.DialogMessageReceived, (message: any) => {
                        onOfficeDialogComplete(message, dialog);
                    });
                    dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: any) => {
                        setLoginStep(OfficeLoginStep.ShowLoginButton);
                        if (arg.error !== 12006) {
                            console.error(`Office Dialog Failed: ${asyncResult.error.code}: + ${asyncResult.error.message}`);
                        }
                    });
                }
            })
    }

    function loginButtonArea(): JSX.Element[] {
        if (loginStep !== OfficeLoginStep.ShowLoginButton) {
            return [<div key="progress" style={{width: "100px", height:"11px"}}>
                    <StatusBar isInfinite={true}/>
                </div>];
        }

        return [
            <PrimaryButton key="lb" onClick={() => {showOfficeDialog(`${Config.baseUrl}/word/dialog/auth/login`)}} className="loginButton">
                <LoginButtonText>LOGIN</LoginButtonText>
            </PrimaryButton>,
            <DgVerticalSpacer height="11px" key="vp3" />,
            <SignupText key="signup" onClick={() => ServerSideRedirect(Config.signupUrl)}>or Sign Up</SignupText>
        ]
    }

    function loginUx(): JSX.Element[] {
        if (loginStep < OfficeLoginStep.Logout) {
            return [
                <TaglineText key="tl1">Welcome to Docugami, the Document Engineering Company!</TaglineText>,
                <TaglineText key="tl2">Docugami for Word helps you create or edit business documents more precisely and efficiently, based on your organization's best practices from previous versions.</TaglineText>,
                <TaglineText key="tl3">To use it, you need to be part of our Early Access Program and have an account. Once our AI trains on your uploaded documents, Docugami for Word is ready to help author your documents!</TaglineText>,
                <DgVerticalSpacer height="40px" key="vp2" />,
                ...loginButtonArea()
            ]
        }

        return []
    }

    function logoutUx(): JSX.Element[] {
        if (loginStep >= OfficeLoginStep.Logout) {
            return [
                <div key="progress" style={{width: "100px", height:"11px"}}>
                    <StatusBar isInfinite={true}/>
                </div>,
                <SignupText key="logout">Logging out</SignupText>
            ]
        }

        return []
    }

    return (
        <LoginContainer>
            <DgFlexCenteredColumn style={{ height: 'auto' }}>
                <DgVerticalSpacer height="50px" key="vp0" />
                <LogoPlaceholder key="logo">
                    <img src='assets/docugamiLogo-160.png' alt='Docugami' />
                </LogoPlaceholder>
                <DgVerticalSpacer height="15px" key="vp1" />
                {loginUx()}
                {logoutUx()}
            </DgFlexCenteredColumn>
        </LoginContainer>
    )
}
